import React from "react";
import styled from "styled-components";
import { Container, Section, Eyebrow } from "../components/ui/Container";
import { PageHeader } from "../components/ui/PageHeader";
import { LinkButton } from "../components/ui/Button";
import { CTA } from "../sections/CTA";
import { colors, fonts, media, radius } from "../theme";

const services = [
  {
    title: "Fintech engineering",
    body: "Mobile and web apps for lenders, SACCOs and payment partners. Built by the team that ships Everpesa.",
    points: ["React, React Native, Node, Python", "PostgreSQL, MongoDB, Redis", "Cloud native on AWS and Azure"],
  },
  {
    title: "Payments and integrations",
    body: "Mobile money, bank APIs, KYC and reconciliation pipelines wired up properly the first time.",
    points: ["MTN MoMo, Airtel Money, Flutterwave", "Bank statement OCR", "Settlement and reconciliation"],
  },
  {
    title: "Credit scoring APIs",
    body: "Plug our alternative credit scoring model into your stack. Score thin file borrowers using mobile money, stock and behaviour signals.",
    points: ["REST API and webhooks", "Tier A to D classification", "Custom scorecards on request"],
  },
  {
    title: "ICT consultancy",
    body: "Advisory for cooperatives, microfinance institutions and government partners on digital transformation.",
    points: ["Digital strategy and architecture", "UMRA and BoU regulatory readiness", "Vendor selection and audits"],
  },
  {
    title: "Applied AI",
    body: "Quietly applied where it matters: smarter credit scoring, fraud detection, document processing, and customer support automation for African contexts.",
    points: ["Risk and fraud models", "Document intelligence", "Production grade and audited"],
  },
  {
    title: "Cybersecurity",
    body: "Practical security for fintech and cooperative environments. Architecture review, penetration testing, and incident readiness.",
    points: ["Security architecture review", "Pen testing and red team", "Incident response readiness"],
  },
];

const stack = [
  "React", "React Native", "Node.js", "Python", "Django", "PostgreSQL",
  "MongoDB", "Redis", "AWS", "Azure", "Docker", "Kubernetes",
  "MTN MoMo", "Airtel Money", "Flutterwave",
];

export const Software = () => (
  <>
    <PageHeader
      eyebrow="Software and ICT services"
      title="The same team that ships Everpesa is also for hire."
      sub="We build production fintech systems that handle real money, real users, and real regulators. Engagements range from short consultancies to multi quarter platform builds for SACCOs, MFIs and partners."
    >
      <LinkButton variant="white" to="/contact">Start a project →</LinkButton>
    </PageHeader>

    <Section>
      <Container>
        <Eyebrow>What we do</Eyebrow>
        <h2 style={{ marginTop: 18, marginBottom: 40, fontFamily: fonts.display, maxWidth: 18 + "ch" }}>
          Engineering and advisory for African finance.
        </h2>

        <Grid>
          {services.map((s) => (
            <Card key={s.title}>
              <h3>{s.title}</h3>
              <p>{s.body}</p>
              <ul>{s.points.map((p) => <li key={p}><Dot />{p}</li>)}</ul>
            </Card>
          ))}
        </Grid>
      </Container>
    </Section>

    <Section style={{ background: colors.surfaceAlt }}>
      <Container>
        <Eyebrow>Stack</Eyebrow>
        <h2 style={{ marginTop: 18, marginBottom: 24, fontFamily: fonts.display, maxWidth: 16 + "ch" }}>
          Modern tools and production grade habits.
        </h2>
        <p style={{ fontSize: 17, marginBottom: 32, maxWidth: 640 }}>
          We pick boring, proven technology and pair it with the regulatory and
          integration know how you need to ship in East Africa.
        </p>
        <Pills>
          {stack.map((s) => <Pill key={s}>{s}</Pill>)}
        </Pills>
      </Container>
    </Section>

    <CTA />
  </>
);

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px;
  ${media.md} { grid-template-columns: 1fr; }
`;

const Card = styled.div`
  background: white;
  border: 1px solid ${colors.line};
  border-radius: ${radius.xl};
  padding: 32px;
  transition: border-color 220ms ease, transform 220ms ease;
  &:hover { border-color: ${colors.brand}; transform: translateY(-3px); }

  h3 { font-family: ${fonts.display}; font-size: 22px; margin-bottom: 12px; }
  p { font-size: 15.5px; margin-bottom: 16px; }
  ul { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 8px; }
  li { display: flex; align-items: center; gap: 10px; font-size: 14.5px; color: ${colors.inkMuted}; }
`;

const Dot = styled.span`
  width: 6px; height: 6px; border-radius: 50%; background: ${colors.brand};
`;

const Pills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const Pill = styled.span`
  background: white;
  border: 1px solid ${colors.line};
  padding: 10px 18px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 500;
  color: ${colors.ink};
  transition: all 200ms ease;
  &:hover { background: ${colors.brand}; color: white; border-color: ${colors.brand}; }
`;
